import styled, { css } from "styled-components";
import { parseSizeToken, useDesignTokens } from "@boi/react-design-tokens";
import { CoreToggle } from "@boi/components-core";

import type { $SizedProps } from "./types";
import { Typography } from "../Typography";

const ANIMATION_DURATION = "0.4s";

type ToggleOuterProps = $SizedProps;

export const ToggleOuter = styled.div<ToggleOuterProps>`
  ${({ $toggleSize }) => {
    const { tokens } = useDesignTokens();
    const { toggle } = tokens;

    const width = parseSizeToken(toggle.size[$toggleSize].width);
    const height = parseSizeToken(toggle.size[$toggleSize].height);

    return css`
      position: relative;
      width: ${width};
      height: ${height};
      display: flex;
      align-items: center;
      border-radius: ${height};

      &:hover:has(input:not(:checked)) {
        background-color: ${toggle.backgroundColor.unchecked.hover};
      }

      &:hover:has(input:checked) {
        background-color: ${toggle.backgroundColor.checked.hover};
      }

      &:has(input:not(:checked)) {
        background-color: ${toggle.backgroundColor.unchecked.base};
      }

      &:has(input:checked) {
        background-color: ${toggle.backgroundColor.checked.base};
      }

      &:has(input:disabled:checked) {
        background-color: ${toggle.backgroundColor.checked.disabled};
      }

      &:has(input:disabled:not(:checked)) {
        background-color: ${toggle.backgroundColor.unchecked.disabled};
      }
    `;
  }}
`;

export const HiddenInput = styled.input`
  cursor: inherit;
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  z-index: 1;
`;

type ToggleInnerProps = $SizedProps;

export const ToggleInner = styled.div<ToggleInnerProps>`
  ${({ $toggleSize }) => {
    const { tokens } = useDesignTokens();
    const { toggle } = tokens;

    const width = parseSizeToken(toggle.size[$toggleSize].width);

    return [
      css`
        position: relative;
        width: ${toggle.thumb.size[$toggleSize]};
        height: ${toggle.thumb.size[$toggleSize]};
        display: flex;
        align-items: center;
        background-color: ${toggle.thumb.backgroundColor.base};
        border-radius: ${toggle.thumb.size[$toggleSize]};
        margin: ${toggle.thumb.padding};
        transition: transform ${ANIMATION_DURATION};

        ${ToggleOuter}:has(input:checked) && {
          transform: translateX(
            calc(
              ${width} - ${toggle.thumb.padding} - ${toggle.thumb.padding} -
                100%
            )
          );
        }

        ${ToggleOuter}:has(input:not(:checked)) && {
          transform: translateX(0px);
        }
      `,
    ];
  }}
`;

type CheckedTextValueProps = $SizedProps;

const useTextPadding = (toggleSize: CoreToggle.Size) => {
  const { tokens } = useDesignTokens();
  const { toggle } = tokens;

  return `${
    parseInt(toggle.thumb.size[toggleSize]) + parseInt(toggle.thumb.padding) * 3
  }px`;
};

export const CheckedTextValue = styled(Typography)<CheckedTextValueProps>`
  ${({ $toggleSize }) => {
    const { tokens } = useDesignTokens();
    const { toggle } = tokens;

    const paddingRight = useTextPadding($toggleSize);

    return [
      css`
        position: absolute;
        width: 100%;
        color: ${toggle.text.color.base};
        text-align: right;
        padding-right: ${paddingRight};
        transition: opacity ${ANIMATION_DURATION};

        ${ToggleOuter}:has(input:disabled) && {
          color: ${toggle.text.color.disabled};
        }

        ${ToggleOuter}:has(input:checked) && {
          opacity: 1;
        }

        ${ToggleOuter}:has(input:not(:checked)) && {
          opacity: 0;
        }
      `,
    ];
  }}
`;

type UncheckedTextValueProps = $SizedProps;

export const UncheckedTextValue = styled(Typography)<UncheckedTextValueProps>`
  ${({ $toggleSize }) => {
    const { tokens } = useDesignTokens();
    const { toggle } = tokens;

    const paddingLeft = useTextPadding($toggleSize);

    return [
      css`
        position: absolute;
        width: 100%;
        color: ${toggle.text.color.base};
        padding-left: ${paddingLeft};
        transition: opacity ${ANIMATION_DURATION};

        ${ToggleOuter}:has(input:disabled) && {
          color: ${toggle.text.color.disabled};
        }

        ${ToggleOuter}:has(input:checked) && {
          opacity: 0;
        }

        ${ToggleOuter}:has(input:not(:checked)) && {
          opacity: 1;
        }
      `,
    ];
  }}
`;

export const TextValuesContainer = styled.div`
  position: absolute;
  height: 100%;
  display: flex;
  width: 100%;
  align-items: center;
`;
